// src/pages/auth/Register.tsx
// Student Registration with Cloudflare R2 direct ID card upload and MSG91 OTP verification.
import { useState, useRef, useMemo, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  User,
  Mail,
  Lock,
  Phone,
  School,
  UploadCloud,
  CheckCircle2,
  Eye,
  EyeOff,
  ArrowRight,
  ShieldCheck,
  Loader2,
  Clock,
  RotateCw,
  AlertCircle,
} from 'lucide-react'
import toast from '@/utils/toast'
import logo from '@/assets/logo2.png'
import loginImage from '@/assets/login_img1.png'
import { CustomSelect, DatePicker, type SelectOption } from '@/components/ui'
import {
  useGetStandardsQuery,
  useLazyLookupInstitutionCodesQuery,
  useLazyGetPresignUploadUrlQuery,
  useVerifyOtpMutation,
  useRegisterStudentMutation,
} from '@/features/student-portal/studentPortal.api'

declare global {
  interface Window {
    configuration?: any
    initSendOTP?: (config: any) => void
    sendOtp?: (
      identifier: string,
      success?: (data: any) => void,
      failure?: (error: any) => void
    ) => void
    verifyOtp?: (
      otp: string,
      success?: (data: any) => void,
      failure?: (error: any) => void,
      reqId?: string | null
    ) => void
    retryOtp?: (
      channel?: any,
      success?: (data: any) => void,
      failure?: (error: any) => void,
      reqId?: string | null
    ) => void
    msg91OtpWidget?: any
  }
}

const MSG91_WIDGET_ID = '366a41693153383135353931'
const MSG91_TOKEN_AUTH = '549453T4L7tgyr6a547eb0P1'
const RESEND_COOLDOWN_SECONDS = 60
const OTP_EXPIRY_SECONDS = 15 * 60 // 15 minutes as configured in MSG91 widget

const GENDER_OPTIONS: SelectOption<'MALE' | 'FEMALE' | 'OTHER'>[] = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
]

export default function Register() {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [otpStep, setOtpStep] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [verifyingOtp, setVerifyingOtp] = useState(false)
  const [verificationToken, setVerificationToken] = useState<string | null>(null)
  const [tempIdCardKey, setTempIdCardKey] = useState<string | null>(null)

  // MSG91 OTP State & Expiration Timers
  const [reqId, setReqId] = useState<string | null>(null)
  const [isResendingOtp, setIsResendingOtp] = useState(false)
  const [resendAvailableAt, setResendAvailableAt] = useState<number | null>(null)
  const [remainingResendSeconds, setRemainingResendSeconds] = useState(0)
  const [otpExpiresAt, setOtpExpiresAt] = useState<number | null>(null)
  const [remainingExpirySeconds, setRemainingExpirySeconds] = useState(0)

  // Form Fields
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [mobile, setMobile] = useState('')
  const [password, setPassword] = useState('')
  const [institutionCode, setInstitutionCode] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [gender, setGender] = useState<'MALE' | 'FEMALE' | 'OTHER'>('MALE')
  const [classStandard, setClassStandard] = useState<string | number>('')

  // ID Card State
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // API Hooks
  const { data: standards = [] } = useGetStandardsQuery()
  const standardOptions: SelectOption<string>[] = useMemo(() => {
    const list = standards.length > 0 ? standards : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
    return list.map((std) => ({
      value: String(std),
      label: `Class ${std}`,
    }))
  }, [standards])

  // Institution Autocomplete State
  const [selectedInstitution, setSelectedInstitution] = useState<{ id?: number; institutionCode: string; name: string } | null>(null)
  const [showInstitutionSuggestions, setShowInstitutionSuggestions] = useState(false)
  const institutionDropdownRef = useRef<HTMLDivElement>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  const [lookupInstitutions, { data: institutionMatches = [], isFetching: isSearchingInstitutions }] = useLazyLookupInstitutionCodesQuery()
  const [getPresignUrl] = useLazyGetPresignUploadUrlQuery()
  const [verifyOtpApi] = useVerifyOtpMutation()
  const [registerStudentApi] = useRegisterStudentMutation()

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (institutionDropdownRef.current && !institutionDropdownRef.current.contains(e.target as Node)) {
        setShowInstitutionSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [])

  const lastInstitutionQueryRef = useRef('')

  // Handle typing institution code with debounce
  const handleInstitutionCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase()
    setInstitutionCode(val)
    setSelectedInstitution(null)

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    const trimmed = val.trim()
    lastInstitutionQueryRef.current = trimmed

    if (!trimmed) {
      setShowInstitutionSuggestions(false)
      return
    }

    setShowInstitutionSuggestions(true)
    debounceTimerRef.current = setTimeout(async () => {
      if (lastInstitutionQueryRef.current !== trimmed) return

      try {
        const res = await lookupInstitutions(trimmed).unwrap()
        if (lastInstitutionQueryRef.current !== trimmed) return

        if (res && res.length > 0) {
          setShowInstitutionSuggestions(true)
          const exact = res.find((i) => i.institutionCode.toUpperCase() === trimmed.toUpperCase())
          if (exact) {
            setSelectedInstitution(exact)
          }
        } else {
          setShowInstitutionSuggestions(false)
        }
      } catch {
        setShowInstitutionSuggestions(false)
      }
    }, 200)
  }

  // Handle selecting an institution from the suggestions dropdown
  const handleSelectInstitution = (item: { id?: number; institutionCode: string; name: string }) => {
    setInstitutionCode(item.institutionCode)
    setSelectedInstitution(item)
    lastInstitutionQueryRef.current = item.institutionCode
    setShowInstitutionSuggestions(false)
  }

  // Handle ID Card selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WebP image.')
      return
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('ID card image must be 2 MB or smaller.')
      return
    }

    setSelectedFile(file)
    setPreviewUrl(URL.createObjectURL(file))
  }

  // Upload raw binary file to Cloudflare R2
  const uploadIdCardToR2 = async (file: File): Promise<string> => {
    const extension = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg'
    const fileName = `student-id-card.${extension}`

    const presignRes = await getPresignUrl({ fileName, contentType: file.type }).unwrap()
    if (!presignRes?.uploadUrl || !presignRes?.tempKey) {
      throw new Error('Could not obtain secure upload URL.')
    }

    // Direct binary PUT (no FormData)
    const uploadRes = await fetch(presignRes.uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    })

    if (!uploadRes.ok) {
      throw new Error('Direct upload to storage failed. Please try again.')
    }

    return presignRes.tempKey
  }

  // Timer countdown hook for OTP expiry and resend
  useEffect(() => {
    if (!otpStep) return

    const updateTimers = () => {
      const now = Date.now()
      if (resendAvailableAt) {
        setRemainingResendSeconds(Math.max(0, Math.ceil((resendAvailableAt - now) / 1000)))
      }
      if (otpExpiresAt) {
        setRemainingExpirySeconds(Math.max(0, Math.ceil((otpExpiresAt - now) / 1000)))
      }
    }

    updateTimers()
    const interval = setInterval(updateTimers, 1000)
    return () => clearInterval(interval)
  }, [otpStep, resendAvailableAt, otpExpiresAt])

  const formatTimer = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60)
    const secs = totalSeconds % 60
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  // Ensure MSG91 script is loaded and configured
  const ensureMsg91Script = async (): Promise<void> => {
    window.configuration = {
      widgetId: MSG91_WIDGET_ID,
      tokenAuth: MSG91_TOKEN_AUTH,
      exposeMethods: true,
      success: (data: any) => {
        console.log('[MSG91 OTP] Success Callback:', data)
      },
      failure: (error: any) => {
        console.error('[MSG91 OTP] Failure Callback:', error)
      },
    }

    if (typeof window.initSendOTP === 'function' && typeof window.sendOtp === 'function') {
      try {
        window.initSendOTP(window.configuration)
      } catch (e) {
        console.warn('[MSG91 OTP] init warning:', e)
      }
      return
    }

    let script = document.querySelector('script[src*="otp-provider.js"]') as HTMLScriptElement | null
    if (!script) {
      script = document.createElement('script')
      script.type = 'text/javascript'
      script.src = 'https://verify.msg91.com/otp-provider.js'
      script.async = true
      document.head.appendChild(script)
    }

    return new Promise((resolve, reject) => {
      if (typeof window.initSendOTP === 'function') {
        try {
          window.initSendOTP(window.configuration)
        } catch (e) {
          console.warn('[MSG91 OTP] init warning:', e)
        }
        resolve()
        return
      }

      const timeout = setTimeout(() => {
        if (typeof window.sendOtp === 'function') {
          resolve()
        } else {
          reject(new Error('OTP provider service took too long to load. Please check your internet connection.'))
        }
      }, 10000)

      script!.addEventListener('load', () => {
        clearTimeout(timeout)
        try {
          if (typeof window.initSendOTP === 'function') {
            window.initSendOTP(window.configuration)
          }
        } catch (e) {
          console.warn('[MSG91 OTP] init on load warning:', e)
        }
        setTimeout(resolve, 300)
      })

      script!.addEventListener('error', () => {
        clearTimeout(timeout)
        reject(new Error('Failed to load OTP verification service. Please disable any adblocker and try again.'))
      })
    })
  }

  // Pre-initialize on mount
  useEffect(() => {
    ensureMsg91Script().catch((err) => console.warn('MSG91 mount init:', err))
  }, [])

  // Handle Form Proceed to Verification
  const handleProceedToVerify = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!firstName.trim() || !lastName.trim() || !email.trim() || !mobile.trim() || !password || !institutionCode.trim()) {
      toast.error('Please complete all required fields.')
      return
    }

    if (!selectedFile && !tempIdCardKey) {
      toast.error('Please upload your Student ID Card image.')
      return
    }

    const cleanMobile = mobile.replace(/^\+/, '').replace(/\D/g, '')
    const digitsOnly = cleanMobile.startsWith('91') && cleanMobile.length > 10 ? cleanMobile.slice(2) : cleanMobile

    if (digitsOnly.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number.')
      return
    }

    try {
      setIsSubmitting(true)
      toast.loading('Uploading ID card & sending verification OTP...', { id: 'reg-flow' })

      // 1. Upload ID card to Cloudflare R2 (if not already uploaded)
      let currentTempKey = tempIdCardKey
      if (!currentTempKey && selectedFile) {
        currentTempKey = await uploadIdCardToR2(selectedFile)
        setTempIdCardKey(currentTempKey)
      }

      // 2. Ensure MSG91 script is initialized
      await ensureMsg91Script()

      // 3. Format mobile for widget: 91 + 10 digits
      const phoneForWidget = `91${digitsOnly}`

      // 4. Send OTP via MSG91 widget
      await new Promise<void>((resolve, reject) => {
        if (typeof window.sendOtp === 'function') {
          window.sendOtp(
            phoneForWidget,
            (res: any) => {
              console.log('[MSG91 OTP] sendOtp response:', res)
              const newReqId =
                res?.reqId ??
                res?.data?.reqId ??
                res?.message ??
                null
              if (newReqId) setReqId(newReqId)
              resolve()
            },
            (err: any) => {
              console.error('[MSG91 OTP] sendOtp error:', err)
              const errMsg = err?.message || (typeof err === 'string' ? err : 'Failed to send OTP SMS. Please try again.')
              reject(new Error(errMsg))
            }
          )
        } else {
          reject(new Error('OTP service is initializing. Please try again in a moment.'))
        }
      })

      // Timers setup
      const now = Date.now()
      setResendAvailableAt(now + RESEND_COOLDOWN_SECONDS * 1000)
      setRemainingResendSeconds(RESEND_COOLDOWN_SECONDS)
      setOtpExpiresAt(now + OTP_EXPIRY_SECONDS * 1000)
      setRemainingExpirySeconds(OTP_EXPIRY_SECONDS)
      setOtpCode('')
      setVerificationToken(null)

      toast.success(`OTP sent to +91 ${digitsOnly}`, { id: 'reg-flow' })
      setOtpStep(true)
    } catch (err: any) {
      toast.error(err?.message || 'Error uploading ID card or sending OTP. Please check your network.', { id: 'reg-flow' })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle Resend OTP via MSG91 widget
  const handleResendOtp = async () => {
    if (remainingResendSeconds > 0 || isResendingOtp) return

    const cleanMobile = mobile.replace(/^\+/, '').replace(/\D/g, '')
    const digitsOnly = cleanMobile.startsWith('91') && cleanMobile.length > 10 ? cleanMobile.slice(2) : cleanMobile
    const phoneForWidget = `91${digitsOnly}`

    try {
      setIsResendingOtp(true)
      toast.loading('Resending OTP...', { id: 'resend-otp' })

      await ensureMsg91Script()

      await new Promise<void>((resolve, reject) => {
        const onResendSuccess = (res: any) => {
          console.log('[MSG91 OTP] retry/resend success:', res)
          const newReqId =
            res?.reqId ??
            res?.data?.reqId ??
            res?.message ??
            reqId
          if (newReqId) setReqId(newReqId)
          resolve()
        }

        const onResendError = (err: any) => {
          // Fallback to sendOtp if retryOtp fails
          if (typeof window.sendOtp === 'function') {
            window.sendOtp(
              phoneForWidget,
              (res: any) => {
                const newReqId = res?.reqId ?? res?.data?.reqId ?? res?.message ?? null
                if (newReqId) setReqId(newReqId)
                resolve()
              },
              (sendErr: any) => {
                const errMsg = sendErr?.message || (typeof sendErr === 'string' ? sendErr : 'Failed to resend OTP.')
                reject(new Error(errMsg))
              }
            )
          } else {
            const errMsg = err?.message || (typeof err === 'string' ? err : 'Failed to resend OTP.')
            reject(new Error(errMsg))
          }
        }

        if (typeof window.retryOtp === 'function') {
          window.retryOtp(null, onResendSuccess, onResendError, reqId)
        } else if (typeof window.sendOtp === 'function') {
          window.sendOtp(phoneForWidget, onResendSuccess, onResendError)
        } else {
          reject(new Error('OTP service unavailable.'))
        }
      })

      const now = Date.now()
      setResendAvailableAt(now + RESEND_COOLDOWN_SECONDS * 1000)
      setRemainingResendSeconds(RESEND_COOLDOWN_SECONDS)
      setOtpExpiresAt(now + OTP_EXPIRY_SECONDS * 1000)
      setRemainingExpirySeconds(OTP_EXPIRY_SECONDS)
      setOtpCode('')

      toast.success('A new OTP has been sent successfully.', { id: 'resend-otp' })
    } catch (err: any) {
      toast.error(err?.message || 'Failed to resend OTP. Please try again.', { id: 'resend-otp' })
    } finally {
      setIsResendingOtp(false)
    }
  }

  // Handle OTP Verification & Registration
  const handleVerifyAndRegister = async () => {
    if (!otpCode.trim() || otpCode.trim().length !== 6) {
      toast.error('Please enter the 6-digit OTP code.')
      return
    }

    if (remainingExpirySeconds <= 0) {
      toast.error('OTP has expired. Please click Resend OTP to receive a new code.')
      return
    }

    const cleanMobile = mobile.replace(/^\+/, '').replace(/\D/g, '')
    const digitsOnly = cleanMobile.startsWith('91') && cleanMobile.length > 10 ? cleanMobile.slice(2) : cleanMobile

    try {
      setVerifyingOtp(true)
      toast.loading('Verifying identity & registering...', { id: 'otp-verify' })

      // 1. Verify OTP with MSG91 widget
      let activeVerificationToken = verificationToken
      if (!activeVerificationToken) {
        const widgetToken = await new Promise<string>((resolve, reject) => {
          if (typeof window.verifyOtp === 'function') {
            window.verifyOtp(
              otpCode.trim(),
              (res: any) => {
                console.log('[MSG91 OTP] verifyOtp response:', res)
                const token =
                  res?.message ??
                  res?.verificationToken ??
                  res?.data?.verificationToken ??
                  res?.token ??
                  (typeof res === 'string' ? res : null)
                if (token) {
                  resolve(token)
                } else {
                  reject(new Error('Verification token not received from widget.'))
                }
              },
              (err: any) => {
                console.error('[MSG91 OTP] verifyOtp error:', err)
                const msg = err?.message || (typeof err === 'string' ? err : 'Invalid OTP. Please check and try again.')
                reject(new Error(msg))
              },
              reqId
            )
          } else {
            reject(new Error('OTP verification service not ready.'))
          }
        })

        // 2. Call backend /auth/otp/verify with accessToken from widget
        const verifyRes = await verifyOtpApi({
          identity: digitsOnly,
          accessToken: widgetToken,
        }).unwrap()

        activeVerificationToken = verifyRes.verificationToken || widgetToken
        setVerificationToken(activeVerificationToken)
      }

      // 3. Complete Student Registration
      await registerStudentApi({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phoneNumber: digitsOnly,
        password,
        institutionCode: institutionCode.trim(),
        dateOfBirth: dateOfBirth || undefined,
        gender,
        classStandard: classStandard || undefined,
        verificationToken: activeVerificationToken,
        idCardImageTempKey: tempIdCardKey || '',
      }).unwrap()

      toast.success('Registration successful! Please sign in.', { id: 'otp-verify' })
      navigate('/login', { replace: true })
    } catch (err: any) {
      const detailsMsg = err?.data?.error?.details?.[0]?.message
      const errorMsg = detailsMsg || err?.data?.message || err?.message || 'Verification or registration failed.'
      toast.error(errorMsg, { id: 'otp-verify' })
    } finally {
      setVerifyingOtp(false)
    }
  }

  return (
    <div className="h-screen w-full bg-white dark:bg-[#070b0e] flex flex-col lg:flex-row font-sans overflow-hidden">
      {/* ── Left Form Container ───────────────────────────────────────── */}
      <div className="w-full lg:w-[58%] h-full flex flex-col px-6 sm:px-10 lg:px-12 py-4 sm:py-5 overflow-y-auto">
        {/* Brand Header — At the top */}
        <div className="shrink-0 -ml-1">
          <Link to="/login" className="inline-flex items-center">
            <img src={logo} alt="Dualon" className="h-20 sm:h-24 w-auto object-contain" />
          </Link>
        </div>

        {/* Form Body */}
        <div className="max-w-xl w-full mx-auto my-auto space-y-2 sm:space-y-2.5">
          <div>
            <h1 className="text-xl sm:text-2xl font-extrabold text-neutral-900 dark:text-white tracking-tight leading-tight">
              Create Your Student Profile
            </h1>
            <p className="text-[11px] sm:text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
              Join your institution's digital learning platform to access syllabus, materials, and exams.
            </p>
          </div>

          {!otpStep ? (
            <form onSubmit={handleProceedToVerify} className="space-y-2 sm:space-y-2.5">
              {/* First & Last Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5">
                <div className="space-y-0.5">
                  <label className="text-[11px] font-semibold text-neutral-700 dark:text-neutral-300">
                    First Name <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <User size={14} className="absolute left-3 top-2.5 text-neutral-400" />
                    <input
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="e.g. John"
                      className="w-full pl-9 pr-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-[#0c1218] text-xs sm:text-sm text-neutral-900 dark:text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>

                <div className="space-y-0.5">
                  <label className="text-[11px] font-semibold text-neutral-700 dark:text-neutral-300">
                    Last Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="e.g. Doe"
                    className="w-full px-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-[#0c1218] text-xs sm:text-sm text-neutral-900 dark:text-white focus:outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5">
                <div className="space-y-0.5">
                  <label className="text-[11px] font-semibold text-neutral-700 dark:text-neutral-300">
                    Email Address <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3 top-2.5 text-neutral-400" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="student@example.com"
                      className="w-full pl-9 pr-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-[#0c1218] text-xs sm:text-sm text-neutral-900 dark:text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>

                <div className="space-y-0.5">
                  <label className="text-[11px] font-semibold text-neutral-700 dark:text-neutral-300">
                    Mobile Number <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone size={14} className="absolute left-3 top-2.5 text-neutral-400" />
                    <input
                      type="tel"
                      required
                      maxLength={10}
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                      placeholder="10-digit number"
                      className="w-full pl-9 pr-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-[#0c1218] text-xs sm:text-sm text-neutral-900 dark:text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>
              </div>

              {/* Password */}
              <div className="space-y-0.5">
                <label className="text-[11px] font-semibold text-neutral-700 dark:text-neutral-300">
                  Password <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-2.5 text-neutral-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a strong password"
                    className="w-full pl-9 pr-9 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-[#0c1218] text-xs sm:text-sm text-neutral-900 dark:text-white focus:outline-none focus:border-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {/* Institution Code & Standard */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5">
                <div className="space-y-0.5 relative" ref={institutionDropdownRef}>
                  <label className="text-[11px] font-semibold text-neutral-700 dark:text-neutral-300 flex items-center justify-between">
                    <span>Institution Code <span className="text-rose-500">*</span></span>
                    {selectedInstitution && (
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                        <CheckCircle2 size={11} />
                        Verified
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <School size={14} className="absolute left-3 top-2.5 sm:top-3 text-neutral-400" />
                    <input
                      type="text"
                      required
                      value={institutionCode}
                      onChange={handleInstitutionCodeChange}
                      onFocus={() => {
                        if (institutionCode.trim().length >= 1 && institutionMatches.length > 0 && !selectedInstitution) {
                          setShowInstitutionSuggestions(true)
                        }
                      }}
                      placeholder="e.g. VZ24"
                      className="w-full pl-9 pr-8 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-[#0c1218] text-xs sm:text-sm text-neutral-900 dark:text-white font-mono uppercase focus:outline-none focus:border-emerald-500"
                    />
                    {isSearchingInstitutions ? (
                      <Loader2 size={14} className="absolute right-3 top-2.5 sm:top-3 text-emerald-500 animate-spin" />
                    ) : selectedInstitution ? (
                      <CheckCircle2 size={15} className="absolute right-3 top-2.5 sm:top-3 text-emerald-500" />
                    ) : null}
                  </div>

                  {/* Autocomplete Suggestions Dropdown matching mobile UI */}
                  {showInstitutionSuggestions && institutionCode.trim().length >= 1 && (
                    <div className="absolute left-0 right-0 top-full mt-1.5 bg-white dark:bg-[#0e161c] border border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-2xl z-50 max-h-52 overflow-y-auto backdrop-blur-md">
                      {isSearchingInstitutions && institutionMatches.length === 0 ? (
                        <div className="p-3 text-center text-xs text-neutral-400 flex items-center justify-center gap-2">
                          <Loader2 size={13} className="animate-spin text-emerald-500" />
                          <span>Searching institutions...</span>
                        </div>
                      ) : institutionMatches.length > 0 ? (
                        <div className="p-1.5 space-y-1">
                          {institutionMatches.map((item) => (
                            <button
                              key={item.id || item.institutionCode}
                              type="button"
                              onClick={() => handleSelectInstitution(item)}
                              className="w-full text-left px-3.5 py-2.5 rounded-xl hover:bg-emerald-50 dark:hover:bg-emerald-500/10 transition-all flex items-center justify-between group cursor-pointer"
                            >
                              <div className="min-w-0 pr-2">
                                <p className="text-xs font-bold text-neutral-900 dark:text-white font-mono uppercase group-hover:text-emerald-600 dark:group-hover:text-emerald-400">
                                  {item.institutionCode}
                                </p>
                                <p className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
                                  {item.name}
                                </p>
                              </div>
                              <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                Select
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="p-3 text-center text-xs text-neutral-400">
                          No institutions found matching "{institutionCode}"
                        </div>
                      )}
                    </div>
                  )}

                  {selectedInstitution && (
                    <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium truncate pt-0.5">
                      Matched: {selectedInstitution.name}
                    </div>
                  )}
                </div>

                <div className="space-y-0.5">
                  <label className="text-[11px] font-semibold text-neutral-700 dark:text-neutral-300">
                    Class / Standard
                  </label>
                  <CustomSelect
                    value={classStandard ? String(classStandard) : ''}
                    onChange={(val) => setClassStandard(val)}
                    options={standardOptions}
                    placeholder="Select Standard"
                  />
                </div>
              </div>

              {/* DOB & Gender */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5">
                <div className="space-y-0.5">
                  <label className="text-[11px] font-semibold text-neutral-700 dark:text-neutral-300">
                    Date of Birth
                  </label>
                  <DatePicker
                    value={dateOfBirth}
                    onChange={(val) => setDateOfBirth(val)}
                    placeholder="Select Date of Birth"
                    disablePastDates={false}
                    maxDate={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="space-y-0.5">
                  <label className="text-[11px] font-semibold text-neutral-700 dark:text-neutral-300">
                    Gender
                  </label>
                  <CustomSelect
                    value={gender}
                    onChange={(val) => setGender(val as 'MALE' | 'FEMALE' | 'OTHER')}
                    options={GENDER_OPTIONS}
                    placeholder="Select Gender"
                  />
                </div>
              </div>

              {/* Student ID Card Upload Box */}
              <div className="space-y-0.5">
                <label className="text-[11px] font-semibold text-neutral-700 dark:text-neutral-300 flex items-center justify-between">
                  <span>Student ID Card Photo <span className="text-rose-500">*</span></span>
                  <span className="text-[10px] text-neutral-400">JPG, PNG, WebP ≤ 2MB</span>
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
                />

                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`p-2 sm:p-2.5 rounded-xl border border-dashed transition-all cursor-pointer flex items-center gap-3 ${
                    selectedFile
                      ? 'border-emerald-500/60 bg-emerald-50/40 dark:bg-emerald-500/5'
                      : 'border-neutral-300 dark:border-neutral-700 hover:border-emerald-500/40 bg-neutral-50/50 dark:bg-neutral-900/30'
                  }`}
                >
                  {previewUrl ? (
                    <img src={previewUrl} alt="ID preview" className="w-10 h-10 rounded-lg object-cover shrink-0 border" />
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-neutral-200/70 dark:bg-neutral-800 flex items-center justify-center text-neutral-500 shrink-0">
                      <UploadCloud size={18} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-neutral-800 dark:text-neutral-200 truncate">
                      {selectedFile ? selectedFile.name : 'Click to select ID Card'}
                    </p>
                    <p className="text-[10px] text-neutral-500">
                      {selectedFile ? `${(selectedFile.size / 1024).toFixed(0)} KB • Ready to upload` : 'Mandatory for institution verification'}
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-1.5 py-2 sm:py-2.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs sm:text-sm tracking-wide shadow-xs transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
              >
                <span>Continue to Phone Verification</span>
                <ArrowRight size={15} />
              </button>

              {/* Already registered — placed directly below button */}
              <div className="text-center pt-2 pb-0.5">
                <span className="text-xs text-neutral-500 dark:text-neutral-400">Already registered? </span>
                <Link to="/login" className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:underline">
                  Sign In
                </Link>
              </div>
            </form>
          ) : (
            /* ── Step 2: OTP Verification ───────────────────────────── */
            <div className="space-y-4 p-5 rounded-2xl bg-neutral-50/80 dark:bg-[#0c1218] border border-neutral-200 dark:border-neutral-800">
              <div className="text-center space-y-1">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center mx-auto mb-1">
                  <ShieldCheck size={22} />
                </div>
                <h3 className="text-sm font-bold text-neutral-900 dark:text-white">Verify Your Mobile Number</h3>
                <p className="text-[11px] text-neutral-500">
                  Enter the 6-digit verification code sent to{' '}
                  <strong className="text-neutral-800 dark:text-neutral-200">
                    +91 {mobile.replace(/^\+/, '').replace(/\D/g, '').replace(/^91(?=\d{10})/, '')}
                  </strong>
                </p>

                {/* Expiration Time indicator */}
                <div className="pt-1.5 flex items-center justify-center">
                  {remainingExpirySeconds > 0 ? (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/80 dark:border-emerald-500/20 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                      <Clock size={12} className="text-emerald-500 animate-pulse" />
                      <span>Code expires in <strong>{formatTimer(remainingExpirySeconds)}</strong></span>
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-50 dark:bg-rose-500/10 border border-rose-200/80 dark:border-rose-500/20 text-[11px] font-medium text-rose-600 dark:text-rose-400">
                      <AlertCircle size={12} />
                      <span>OTP has expired. Please resend a new code below.</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-semibold text-neutral-700 dark:text-neutral-300">
                    Verification OTP Code
                  </label>
                  <span className="text-[10px] text-neutral-400 font-mono">6 Digits</span>
                </div>
                <input
                  type="text"
                  maxLength={6}
                  autoFocus
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="• • • • • •"
                  className="w-full text-center tracking-[0.4em] text-lg font-mono py-2.5 px-3 rounded-xl border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-[#070b0e] text-neutral-900 dark:text-white focus:outline-none focus:border-emerald-500 placeholder:tracking-normal placeholder:font-sans placeholder:text-neutral-400"
                />
              </div>

              {/* Resend OTP Row with Cooldown Timer */}
              <div className="flex items-center justify-between pt-0.5 px-0.5 text-xs">
                <span className="text-neutral-500 dark:text-neutral-400 text-[11px]">
                  Didn't receive the code?
                </span>
                {remainingResendSeconds > 0 ? (
                  <span className="text-[11px] font-medium text-neutral-400 flex items-center gap-1">
                    <Clock size={11} />
                    Resend in {formatTimer(remainingResendSeconds)}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={isResendingOtp}
                    className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    {isResendingOtp ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <RotateCw size={12} />
                    )}
                    <span>Resend OTP</span>
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setOtpStep(false)}
                  className="flex-1 py-2 sm:py-2.5 rounded-xl border border-neutral-300 dark:border-neutral-700 text-xs font-semibold text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors cursor-pointer"
                >
                  Back to Form
                </button>
                <button
                  type="button"
                  onClick={handleVerifyAndRegister}
                  disabled={verifyingOtp || otpCode.trim().length !== 6 || remainingExpirySeconds <= 0}
                  className="flex-1 py-2 sm:py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shadow-xs transition-all disabled:opacity-50 flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  {verifyingOtp ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <span>Verify & Register</span>
                  )}
                </button>
              </div>

              {/* Already registered — placed directly below OTP buttons */}
              <div className="text-center pt-2">
                <span className="text-xs text-neutral-500 dark:text-neutral-400">Already registered? </span>
                <Link to="/login" className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:underline">
                  Sign In
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Right Hero Image Container ─────────────────────────────────── */}
      <div className="hidden lg:flex lg:w-[42%] relative bg-[#010519] flex-col justify-end overflow-hidden">
        <img
          src={loginImage}
          alt="Registration Visual"
          loading="eager"
          decoding="async"
          className="absolute inset-0 w-full h-full object-contain object-right-top pl-8 z-0 transition-transform duration-700 hover:scale-102"
        />
        <div className="relative z-10 p-10 lg:p-14 max-w-2xl text-left bg-gradient-to-t from-[#010519] via-[#010519]/75 to-transparent">
          <h2 className="text-3xl lg:text-4xl font-semibold text-white tracking-tight leading-tight">
            Advanced Learning, <br /> Built for You.
          </h2>
          <p className="mt-4 text-sm lg:text-base text-gray-400 max-w-xl leading-relaxed">
            Dualon is your unified hub for digital education. Access institution-curated syllabus, comprehensive study resources, and real-time exam schedules with a platform designed to empower your learning journey.
          </p>
        </div>
      </div>
    </div>
  )
}
